import { useTranslation } from 'react-i18next';
import { SearchInput } from '~/components/common/SearchInput';
import { RetryBanner } from '~/components/ui/RetryBanner';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useGetTradingPartnerOrganizationsForBuyer } from '~/api/organizations';
import { sellerLabel } from '~/utils';

export type SellerSelectTableLabels = {
  organization: string;
  searchPlaceholder: string;
  searchLabel: string;
  loading: string;
  empty: string;
  noResults?: string;
  error: string;
  select: (name: string) => string;
};

type SellerSelectTableProps = {
  testId: string;
  labels: SellerSelectTableLabels;
  selectedIds: Set<string>;
  onToggle: (organizationId: string) => void;
  excludeOrganizationIds?: string[];
};

const NO_EXCLUDED_ORGANIZATION_IDS: string[] = [];

/**
 * Searchable, paged table of the buyer's trading partners with a checkbox per row.
 * The caller owns the selection.
 */
export function SellerSelectTable({
  testId,
  labels,
  selectedIds,
  onToggle,
  excludeOrganizationIds = NO_EXCLUDED_ORGANIZATION_IDS,
}: Readonly<SellerSelectTableProps>) {
  const { t } = useTranslation('general');
  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();
  const {
    data: organizations,
    sentinelRef,
    isLoading,
    error,
    refetch,
  } = usePaginatedQuery({
    fetchHook: useGetTradingPartnerOrganizationsForBuyer,
    select: (data) => data?.tradingPartnerOrganizationsForBuyer,
    options: {
      pageSize: 20,
      filters: { search: activeSearch, excludeOrganizationIds },
    },
  });
  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { key: 'name', header: labels.organization, width: 20 },
    { key: 'select', header: '', width: 4 },
  ];

  const rows: TableRow[] = organizations.map((organization) => ({
    id: { value: organization.id },
    select: {
      value: (
        <input
          type="checkbox"
          checked={selectedIds.has(organization.id)}
          onChange={() => onToggle(organization.id)}
          aria-label={labels.select(sellerLabel(organization))}
          data-testid={`${testId}-checkbox-${organization.id}`}
        />
      ),
    },
    name: { value: sellerLabel(organization) },
  }));

  return (
    <>
      <div className="px-6 py-4 border-b border-neut-border shrink-0">
        <SearchInput
          value={searchInputValue}
          onChange={handleSearchChange}
          placeholder={labels.searchPlaceholder}
          ariaLabel={labels.searchLabel}
          testId={`${testId}-search`}
          widthClassName="max-w-xs"
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <RetryBanner
            message={labels.error}
            retryLabel={t('general.retry')}
            onRetry={() => void refetch()}
            testId={testId}
          />
        ) : isLoading && organizations.length === 0 ? (
          <div className="p-8 text-center text-neut-sec text-sm">
            {labels.loading}
          </div>
        ) : (
          <Table
            minWidth={30}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            emptyMessage={
              activeSearch.trim()
                ? (labels.noResults ?? t('searchable_select.no_results'))
                : labels.empty
            }
            name={`${testId}-organizations`}
          />
        )}
        <div ref={sentinelRef} />
      </div>
    </>
  );
}
